import { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useSafeLandingPath } from '@/hooks/useNavAccess';

// ─── Account group ───────────────────────────────────────────────────────────
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { AccountDetailsAdmin } from '@/pages/admin/AccountDetailsAdmin';
import { BillingMethodsAdmin } from '@/pages/admin/BillingMethodsAdmin';
import { AccessRestrictionsAdmin } from '@/pages/admin/AccessRestrictionsAdmin';
import { PatientPortalAdmin } from '@/pages/admin/PatientPortalAdmin';
import { KnowledgeBaseAdmin } from '@/pages/admin/KnowledgeBaseAdmin';
import { ScorecardsAdmin } from '@/pages/admin/ScorecardsAdmin';
import { ScorecardEditorPage } from '@/pages/admin/ScorecardEditorPage';

// ─── Practice group ──────────────────────────────────────────────────────────
import { LanguagesAdmin } from '@/pages/admin/LanguagesAdmin';
import { TasksAdmin } from '@/pages/admin/TasksAdmin';
import { TaskGroupsAdmin } from '@/pages/admin/TaskGroupsAdmin';
import { VipProgramAdmin } from '@/pages/admin/VipProgramAdmin';
import { PatientTagsAdmin } from '@/pages/admin/PatientTagsAdmin';
import { FacilitiesAdmin } from '@/pages/admin/FacilitiesAdmin';
import { LeadSourcesAdmin } from '@/pages/admin/LeadSourcesAdmin';
import { LeadFieldsAdmin } from '@/pages/admin/LeadFieldsAdmin';
import { NewLeadFormAdmin } from '@/pages/admin/NewLeadFormAdmin';
import { ProceduresAdmin } from '@/pages/admin/ProceduresAdmin';
import { ProductsAdmin } from '@/pages/admin/ProductsAdmin';
import { ProductTypesAdmin } from '@/pages/admin/ProductTypesAdmin';
import { GetLabsAdmin } from '@/pages/admin/GetLabsAdmin';
import { RmmAdmin } from '@/pages/admin/RmmAdmin';
import { SiphoxAdmin } from '@/pages/admin/SiphoxAdmin';
import { BodySpecAdmin } from '@/pages/admin/BodySpecAdmin';
import { AxiomAdmin } from '@/pages/admin/AxiomAdmin';
import { TruDiagnosticAdmin } from '@/pages/admin/TruDiagnosticAdmin';
import { MescreenAdmin } from '@/pages/admin/MescreenAdmin';
import { GenovaAdmin } from '@/pages/admin/GenovaAdmin';
import { VibrantAdmin } from '@/pages/admin/VibrantAdmin';
import { AccessAdmin } from '@/pages/admin/AccessAdmin';
import { ACCESS_LABS_MODULE_KEY } from '@/lib/accessLabs';
import { QuestAdmin } from '@/pages/admin/QuestAdmin';
import { LabUploadsAdmin } from '@/pages/admin/LabUploadsAdmin';
import { TreatRxAdmin } from '@/pages/admin/TreatRxAdmin';
import { WhatsAppAdmin } from '@/pages/admin/WhatsAppAdmin';
import { TwilioSettingsAdmin } from '@/pages/admin/TwilioSettingsAdmin';
import RequireModule from '@/components/layout/RequireModule';
import RequireAdminPage from '@/components/layout/RequireAdminPage';
import RequirePermission from '@/components/layout/RequirePermission';
import { PERMISSION } from '@/lib/permissions';
// Lazy-loaded: the Vitaboom SDK pulls in MUI + Emotion (~a few hundred KB), so
// the Marketplace page gets its own chunk that only Vitaboom-enabled admins
// ever download (same treatment as the patient Vitamins tab).
const MarketplaceAdmin = lazyWithRetry(
  () => import('@/pages/admin/MarketplaceAdmin').then((m) => ({ default: m.MarketplaceAdmin })),
  'marketplace-admin',
);
import { AiSettingsAdmin } from '@/pages/admin/AiSettingsAdmin';
import { GoogleCalendarSettingsAdmin } from '@/pages/admin/GoogleCalendarSettingsAdmin';
import { MembershipTemplatesAdmin } from '@/pages/admin/MembershipTemplatesAdmin';
import { PackagesAdmin } from '@/pages/admin/PackagesAdmin';
import { MembershipAgreementsAdmin } from '@/pages/admin/MembershipAgreementsAdmin';
import { PaymentMethodsAdmin } from '@/pages/admin/PaymentMethodsAdmin';
import { QuotePresetsAdmin } from '@/pages/admin/QuotePresetsAdmin';
import { QuoteDiscountsAdmin } from '@/pages/admin/QuoteDiscountsAdmin';
import { LoanTypesAdmin } from '@/pages/admin/LoanTypesAdmin';
import { LabDecksAdmin } from '@/pages/admin/LabDecksAdmin';
import { QaReasonsAdmin } from '@/pages/admin/QaReasonsAdmin';
import { MembershipCancelReasonsAdmin } from '@/pages/admin/MembershipCancelReasonsAdmin';
import { ShippingFeesAdmin } from '@/pages/admin/ShippingFeesAdmin';
import { PlanTypesAdmin } from '@/pages/admin/PlanTypesAdmin';
import { CommonLinksAdmin } from '@/pages/admin/CommonLinksAdmin';
import { AppointmentResourcesAdmin } from '@/pages/admin/AppointmentResourcesAdmin';
import { BrandingAdmin } from '@/pages/admin/BrandingAdmin';
import { PromoCodesAdmin } from '@/pages/admin/PromoCodesAdmin';

// ─── Plans group ─────────────────────────────────────────────────────────────
import { PlanRolesAdmin } from '@/pages/admin/PlanRolesAdmin';
import { PlanStatusesAdmin } from '@/pages/admin/PlanStatusesAdmin';
import { StagePipelineAdmin } from '@/pages/admin/StagePipelineAdmin';

// ─── Appointments group ──────────────────────────────────────────────────────
import { AppointmentSettingsAdmin } from '@/pages/admin/AppointmentSettingsAdmin';
import { AppointmentConfirmationsAdmin } from '@/pages/admin/AppointmentConfirmationsAdmin';
import { TimeBlocksAdmin } from '@/pages/admin/TimeBlocksAdmin';
import { CalendarSettingsAdmin } from '@/pages/admin/CalendarSettingsAdmin';
import { GiftCardSettingsAdmin } from '@/pages/admin/GiftCardSettingsAdmin';

// ─── Payments group ──────────────────────────────────────────────────────────
import { PaymentCreditTypesAdmin } from '@/pages/admin/PaymentCreditTypesAdmin';
import { PaymentDiscountsAdmin } from '@/pages/admin/PaymentDiscountsAdmin';
import { PosTerminalsAdmin } from '@/pages/admin/PosTerminalsAdmin';
import { ForwardMerchantsAdmin } from '@/pages/admin/ForwardMerchantsAdmin';

// ─── Users group ─────────────────────────────────────────────────────────────
import { UsersAdmin } from '@/pages/admin/UsersAdmin';
import { UserGroupsAdmin } from '@/pages/admin/UserGroupsAdmin';
import { UserAuditAdmin } from '@/pages/admin/UserAuditAdmin';
import { SupportTicketsAdmin } from '@/pages/admin/SupportTicketsAdmin';
import { AccountAlertsAdmin } from '@/pages/admin/AccountAlertsAdmin';
import { AlertsAdmin } from '@/pages/admin/AlertsAdmin';

// ─── Import group (data migration; corp super admins only) ───────────────────
import { ImportAdmin } from '@/pages/admin/ImportAdmin';
import { ZenotiImportPage } from '@/pages/admin/ZenotiImportPage';
import { LeadsImportPage } from '@/pages/admin/LeadsImportPage';
import { LEAD_IMPORT_MODULE_KEY } from '@/lib/leadImport';

// ─── Templates group ─────────────────────────────────────────────────────────
import { DocumentsAdmin } from '@/pages/admin/DocumentsAdmin';
import { PrintTemplatesAdmin } from '@/pages/admin/PrintTemplatesAdmin';
import { DisclosuresAdmin } from '@/pages/admin/DisclosuresAdmin';
import NoteTemplatesAdmin from '@/pages/admin/NoteTemplatesAdmin';
import { EmailTemplatesAdmin } from '@/pages/admin/EmailTemplatesAdmin';
import { EmailHeaderFooterAdmin } from '@/pages/admin/EmailHeaderFooterAdmin';
import { SmsTemplatesAdmin } from '@/pages/admin/SmsTemplatesAdmin';
import { AutoRespondersAdmin } from '@/pages/admin/AutoRespondersAdmin';
import { EFormsAdmin } from '@/pages/admin/EFormsAdmin';

// ─── Reports group ───────────────────────────────────────────────────────────

// ─── Marketing group ─────────────────────────────────────────────────────────
import MarketingIntegrationsAdmin from '@/pages/admin/MarketingIntegrationsAdmin';

// ─── Dev tools ──────────────────────────────────────────────────────────────

function RequireAdmin({ children }: { children: JSX.Element }) {
  const { isAdmin } = useAuth();
  const location = useLocation();

  if (!isAdmin) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

// The Admin area itself: level 5, or the Admin Access permission (36). User
// level below 5 no longer opens it — the old level-3 / level-4 split is gone.
// A layout route, so every practice-admin route below sits behind one gate.
function RequireAdminAccess() {
  const { hasAdminAccess } = useAuth();
  const location = useLocation();

  if (!hasAdminAccess) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

function RequireSuperAdmin({ children }: { children: JSX.Element }) {
  const { isSuperAdmin } = useAuth();
  const location = useLocation();

  if (!isSuperAdmin) {
    return <Navigate to="/help" state={{ from: location }} replace />;
  }

  return children;
}

// Gates Knowledge Base management on the corporate master_users.is_admin
// flag, not userLevel — this is corp-internal content curated by Prospera
// admins, not a per-practice setting. Only sessions from the corp SSO
// bridge ever carry a master_user identity, so direct practice logins
// (regardless of userLevel) never satisfy this.
function RequireMasterAdmin({ children }: { children: JSX.Element }) {
  const { isMasterAdmin } = useAuth();
  const location = useLocation();

  if (!isMasterAdmin) {
    return <Navigate to="/help" state={{ from: location }} replace />;
  }

  return children;
}

// /admin and any unmatched /admin/... URL go to the first page the account can
// see — never a Hidden Admin page, since the safe landing comes from the
// level-filtered nav.
function AdminFallback() {
  return <Navigate to={useSafeLandingPath()} replace />;
}

export function AdminPage() {
  return (
    <AdminRoutes />
  );
}

function AdminRoutes() {
  return (
    <Routes>
      {/* Corp-only tools that live under /admin but are NOT the practice Admin
          area: they sit in their own nav groups (Knowledge Base, Support) and
          keep their old gates — level 3 plus the corp flag — instead of
          Admin Access. */}
      <Route element={<RequireAdmin><Outlet /></RequireAdmin>}>
      <Route
        path="knowledge-base/*"
        element={(
          <RequireMasterAdmin>
            <KnowledgeBaseAdmin />
          </RequireMasterAdmin>
        )}
      />
      <Route
        path="support-tickets"
        element={(
          <RequireSuperAdmin>
            <SupportTicketsAdmin />
          </RequireSuperAdmin>
        )}
      />
      {/* Announcements authoring (Prospera -> practices). Corp-only, like the
          Support queue — surfaced under the Support nav group. */}
      <Route
        path="account-alerts"
        element={(
          <RequireSuperAdmin>
            <AccountAlertsAdmin />
          </RequireSuperAdmin>
        )}
      />
      </Route>

      <Route element={<RequireAdminAccess />}>
      {/* Every practice Admin page also follows the account's level for it
          (RequireAdminPage, keyed by the Admin page catalog — see
          adminPages.drift.test.ts): Hidden sends the URL to the safe landing,
          Locked renders the page read-only. A level only narrows the gates
          around it. Aliases and sub-pages sit under their entry's key. */}

      {/* Account group */}
      {/* The account's settings live on Details (the old page here was a
          placeholder that saved nowhere); /admin/account is its alias. */}
      <Route element={<RequireAdminPage page="account-summary" />}>
        <Route path="account" element={<Navigate to="/admin/account-summary" replace />} />
        <Route path="account-summary" element={<AccountDetailsAdmin />} />
      </Route>
      {/* The practice's own dashboard alerts (corp's Account Alerts); Prospera's
          group-wide announcements stay under Support (account-alerts). */}
      <Route element={<RequireAdminPage page="alerts" />}>
        <Route path="alerts" element={<AlertsAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="billing-methods" />}>
        <Route path="billing-methods" element={<BillingMethodsAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="access-restrictions" />}>
        <Route path="access-restrictions" element={<AccessRestrictionsAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="crm" />}>
        <Route element={<RequireAdminPage page="scorecards" />}>
          <Route path="scorecards" element={<ScorecardsAdmin />} />
          <Route path="scorecards/:id" element={<ScorecardEditorPage />} />
        </Route>
      </Route>
      <Route element={<RequireAdminPage page="patient-portal" />}>
        <Route path="patient-portal" element={<PatientPortalAdmin />} />
      </Route>

      {/* Practice group */}
      <Route element={<RequireAdminPage page="languages" />}>
        <Route path="languages" element={<LanguagesAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="vip-program" />}>
        <Route path="vip-program" element={<VipProgramAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="patient-tags" />}>
        <Route path="patient-tags" element={<PatientTagsAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="facilities" requireActive />}>
        <Route element={<RequireAdminPage page="facilities" />}>
          <Route path="facilities" element={<FacilitiesAdmin />} />
        </Route>
      </Route>
      {/* /admin/referrers is the old Lead Sources URL (the nav still links it). */}
      <Route element={<RequireAdminPage page="lead-sources" />}>
        <Route path="lead-sources" element={<LeadSourcesAdmin />} />
        <Route path="referrers" element={<Navigate to="/admin/lead-sources" replace />} />
      </Route>
      <Route element={<RequireModule moduleKey="crm" />}>
        <Route element={<RequireAdminPage page="lead-fields" />}>
          <Route path="lead-fields" element={<LeadFieldsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="lead-form" />}>
          <Route path="lead-form" element={<NewLeadFormAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="scheduling" />}>
        <Route element={<RequireAdminPage page="procedures" />}>
          <Route path="procedures" element={<ProceduresAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="inventory" />}>
        <Route element={<RequireAdminPage page="products" />}>
          <Route path="products" element={<ProductsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="product-types" />}>
          <Route path="product-types" element={<ProductTypesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="shipping-fees" />}>
          <Route path="shipping-fees" element={<ShippingFeesAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireAdminPage page="integrations/getlabs" />}>
        <Route path="integrations/getlabs" element={<GetLabsAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="integrations/quest" />}>
        <Route path="integrations/quest" element={<QuestAdmin />} />
      </Route>
      {/* Corp super admins only: an integration lets an outside system file
          labs on this practice's charts. The API enforces the same gate. No
          level: super-admin-only pages keep their own gate alone. */}
      <Route
        path="integrations/lab-uploads"
        element={
          <RequireSuperAdmin>
            <LabUploadsAdmin />
          </RequireSuperAdmin>
        }
      />

      {/* ── Marketplace vendors ────────────────────────────────────────────*/}
      <Route element={<RequireAdminPage page="marketplace/siphox" />}>
        <Route path="marketplace/siphox" element={<SiphoxAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="marketplace/bodyspec" />}>
        <Route path="marketplace/bodyspec" element={<BodySpecAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="axiom" />}>
        <Route element={<RequireAdminPage page="marketplace/axiom" />}>
          <Route path="marketplace/axiom" element={<AxiomAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireAdminPage page="marketplace/trudiagnostic" />}>
        <Route path="marketplace/trudiagnostic" element={<TruDiagnosticAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="marketplace/mescreen" />}>
        <Route path="marketplace/mescreen" element={<MescreenAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="marketplace/genova" />}>
        <Route path="marketplace/genova" element={<GenovaAdmin />} />
      </Route>
      {/* Gated like Axiom: Partell dispenses controlled substances, so the
          page must not be reachable for a practice that has no such
          agreement — the module row is what says it does. */}
      <Route element={<RequireModule moduleKey="partell" />}>
        <Route element={<RequireAdminPage page="marketplace/rmm" />}>
          <Route path="marketplace/rmm" element={<RmmAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="vibrant ordering" requireActive />}>
        <Route element={<RequireAdminPage page="marketplace/vibrant" />}>
          <Route path="marketplace/vibrant" element={<VibrantAdmin />} />
        </Route>
      </Route>
      {/* Fail-closed like Vibrant ordering: Access Labs is opt-in. */}
      <Route element={<RequireModule moduleKey={ACCESS_LABS_MODULE_KEY} requireActive />}>
        <Route element={<RequireAdminPage page="integrations/access" />}>
          <Route path="integrations/access" element={<AccessAdmin />} />
        </Route>
      </Route>
      {/* Fail-closed like Facilities/Vibrant: the marketplace is opt-in, so it
          requires an explicitly ACTIVE 'vitaboom' module row. */}
      <Route element={<RequireModule moduleKey="vitaboom" requireActive />}>
        <Route element={<RequireAdminPage page="marketplace/vitaboom" />}>
          <Route
            path="marketplace/vitaboom"
            element={(
              <Suspense fallback={<div className="flex justify-center py-16"><Spinner /></div>}>
                <MarketplaceAdmin />
              </Suspense>
            )}
          />
        </Route>
      </Route>

      <Route element={<RequireModule moduleKey="treatrx" />}>
        <Route element={<RequireAdminPage page="integrations/treatrx" />}>
          <Route path="integrations/treatrx" element={<TreatRxAdmin />} />
        </Route>
      </Route>
      {/*
        The practice's WhatsApp sender. Fail-open ON PURPOSE — no requireActive.
        This page is how a practice enters the number in the first place, so
        gating it on the module already being active locked setup behind itself.
        Sending stays fail-closed; only an explicitly DISABLED account is
        refused here, via config/module_gates.php.
      */}
      <Route element={<RequireModule moduleKey="whatsapp" />}>
        <Route element={<RequireAdminPage page="integrations/whatsapp" />}>
          <Route path="integrations/whatsapp" element={<WhatsAppAdmin />} />
        </Route>
      </Route>
      {/* The practice's own Twilio account (legacy Twilio Settings). */}
      <Route element={<RequireAdminPage page="integrations/twilio" />}>
        <Route path="integrations/twilio" element={<TwilioSettingsAdmin />} />
      </Route>
      {/* The per-account AWS Bedrock credentials the assistant runs on. */}
      <Route element={<RequireModule moduleKey="ai assistant" />}>
        <Route element={<RequireAdminPage page="integrations/ai" />}>
          <Route path="integrations/ai" element={<AiSettingsAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireAdminPage page="integrations/google-calendar" />}>
        <Route path="integrations/google-calendar" element={<GoogleCalendarSettingsAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="memberships" />}>
        <Route element={<RequireAdminPage page="memberships" />}>
          <Route path="memberships" element={<MembershipTemplatesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="membership-agreements" />}>
          <Route path="membership-agreements" element={<MembershipAgreementsAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="payments" />}>
        <Route element={<RequireAdminPage page="payment-methods" />}>
          <Route path="payment-methods" element={<PaymentMethodsAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="tasks" />}>
        <Route element={<RequireAdminPage page="tasks" />}>
          <Route path="tasks" element={<TasksAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="task-groups" />}>
          <Route path="task-groups" element={<TaskGroupsAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="packages" />}>
        <Route element={<RequireAdminPage page="packages" />}>
          <Route path="packages" element={<PackagesAdmin />} />
        </Route>
      </Route>
      {/* Opt-in like the nav entries (requiresModuleActive: 'quotes'). */}
      <Route element={<RequireModule moduleKey="quotes" requireActive />}>
        <Route element={<RequireAdminPage page="quote-presets" />}>
          <Route path="quote-presets" element={<QuotePresetsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="quote-discounts" />}>
          <Route path="quote-discounts" element={<QuoteDiscountsAdmin />} />
        </Route>
      </Route>
      {/* Lab Decks is hidden for now (see navigation.config.ts): 'lab-decks' is
          not in the catalog, so no account has a level and this redirects. */}
      <Route element={<RequireAdminPage page="lab-decks" />}>
        <Route path="lab-decks" element={<LabDecksAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="qa" />}>
        <Route element={<RequireAdminPage page="qa-reasons" />}>
          <Route path="qa-reasons" element={<QaReasonsAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="memberships" />}>
        <Route element={<RequireAdminPage page="membership-cancel-reasons" />}>
          <Route path="membership-cancel-reasons" element={<MembershipCancelReasonsAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireModule moduleKey="crm" />}>
        <Route element={<RequireAdminPage page="plan-types" />}>
          <Route path="plan-types" element={<PlanTypesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="plan-statuses" />}>
          <Route path="plan-statuses" element={<PlanStatusesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="stages" />}>
          <Route path="stages" element={<StagePipelineAdmin />} />
        </Route>
      </Route>
      <Route element={<RequireAdminPage page="common-links" />}>
        <Route path="common-links" element={<CommonLinksAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="branding" />}>
        <Route path="branding" element={<BrandingAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="promo codes" />}>
        <Route element={<RequireAdminPage page="promo-codes" />}>
          <Route path="promo-codes" element={<PromoCodesAdmin />} />
        </Route>
      </Route>

      {/* Plans group */}
      <Route element={<RequireAdminPage page="plan-roles" />}>
        <Route path="plan-roles" element={<PlanRolesAdmin />} />
      </Route>

      {/* Appointments group */}
      <Route element={<RequireModule moduleKey="scheduling" />}>
        <Route element={<RequireAdminPage page="appointments" />}>
          <Route path="appointments" element={<AppointmentSettingsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="appointment-confirmations" />}>
          <Route path="appointment-confirmations" element={<AppointmentConfirmationsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="calendar-settings" />}>
          <Route path="calendar-settings" element={<CalendarSettingsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="appointment-resources" />}>
          <Route path="appointment-resources" element={<AppointmentResourcesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="time-blocks" />}>
          <Route path="time-blocks" element={<TimeBlocksAdmin />} />
        </Route>
      </Route>

      {/* Payments group */}
      <Route element={<RequireModule moduleKey="discounts" />}>
        <Route element={<RequireAdminPage page="payment-discounts" />}>
          <Route path="payment-discounts" element={<PaymentDiscountsAdmin />} />
        </Route>
      </Route>
      {/* Forward merchant accounts — the gateway credentials. Payments-module
          gated as well as admin: the admin level alone is not enough for a
          practice that takes no cards. Payouts and Disputes are NOT here; they
          live under /payments with a permission each. */}
      <Route element={<RequireModule moduleKey="payments" />}>
        <Route element={<RequireAdminPage page="payments/forward" />}>
          <Route path="payments/forward" element={<ForwardMerchantsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="credit-types" />}>
          <Route path="credit-types" element={<PaymentCreditTypesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="loan-types" />}>
          <Route path="loan-types" element={<LoanTypesAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="pos-terminals" />}>
          <Route path="pos-terminals" element={<PosTerminalsAdmin />} />
        </Route>
        <Route element={<RequireAdminPage page="gift-card-settings" />}>
          <Route path="gift-card-settings" element={<GiftCardSettingsAdmin />} />
        </Route>
      </Route>

      {/* Users group */}
      <Route element={<RequireAdminPage page="users" />}>
        <Route path="users" element={<UsersAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="user groups" />}>
        <Route element={<RequireAdminPage page="user-groups" />}>
          <Route path="user-groups" element={<UserGroupsAdmin />} />
        </Route>
      </Route>
      {/* The page's level, plus the permission (R18). The API also requires
          level 3 and the grant, live, on every list and download. */}
      <Route element={<RequireAdminPage page="user-audit" />}>
        <Route element={<RequirePermission permissionId={PERMISSION.USER_AUDIT} fallback="/admin/users" />}>
          <Route path="user-audit" element={<UserAuditAdmin />} />
        </Route>
      </Route>
      {/* Adding leads from a spreadsheet writes only into the caller's own
          account, so it sits OUTSIDE the Zenoti gate below. RequireAdminAccess
          already wraps this whole tree, and the API applies its own
          level and module checks — these guards are the UX, not the
          boundary. Two gates: `crm`, to match the Leads list it feeds, and the
          opt-in Lead Import module. `holdOnceAllowed` checks that module on
          entry only, so a modules refetch mid-import cannot redirect away from
          a running import and lose its summary; RequireAdminPage holds the
          page the same way. */}
      <Route element={<RequireModule moduleKey="crm" />}>
        <Route element={<RequireModule moduleKey={LEAD_IMPORT_MODULE_KEY} requireActive holdOnceAllowed />}>
          <Route element={<RequireAdminPage page="import/leads" />}>
            <Route path="import/leads" element={<LeadsImportPage />} />
          </Route>
        </Route>
      </Route>
      {/* Data-migration tools. Corp super admins only — the API enforces the
          same gate on every /v5/import-runs route, so this guard is the UX,
          not the security boundary. No level: super-admin-only pages keep
          their own gate alone. */}
      {/* Super-admin AND the module: the strict flag is the security boundary,
          the module lets a practice have the tool switched off entirely.
          `requireActive` makes the module fail CLOSED: it needs an ACTIVE
          aa_accounts_modules row from a fresh /account/modules response, so
          neither a never-toggled account nor a stale persisted seed can open
          the tool. */}
      <Route element={<RequireModule moduleKey="zenoti import" requireActive />}>
        <Route
          path="import"
          element={(
            <RequireSuperAdmin>
              <ImportAdmin />
            </RequireSuperAdmin>
          )}
        />
        <Route
          path="import/zenoti"
          element={(
            <RequireSuperAdmin>
              <ZenotiImportPage />
            </RequireSuperAdmin>
          )}
        />
      </Route>

      {/* Templates group */}
      <Route element={<RequireAdminPage page="documents" />}>
        <Route path="documents" element={<DocumentsAdmin />} />
      </Route>
      {/* Packets are part of Documents now, as in legacy. */}
      <Route path="document-packets" element={<Navigate to="/admin/documents" replace />} />
      <Route element={<RequireAdminPage page="print-templates" />}>
        <Route path="print-templates" element={<PrintTemplatesAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="disclosures" />}>
        <Route path="disclosures" element={<DisclosuresAdmin />} />
      </Route>
      <Route element={<RequireModule moduleKey="messaging" />}>
        <Route element={<RequireAdminPage page="note-templates" />}>
          <Route path="note-templates" element={<NoteTemplatesAdmin />} />
        </Route>
      </Route>
      {/* The account's own SMS / email templates, not Marketing's: no module. */}
      <Route element={<RequireAdminPage page="email-templates" />}>
        <Route path="email-templates" element={<EmailTemplatesAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="sms-templates" />}>
        <Route path="sms-templates" element={<SmsTemplatesAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="email-header-footer" />}>
        <Route path="email-header-footer" element={<EmailHeaderFooterAdmin />} />
      </Route>
      <Route element={<RequireAdminPage page="auto-responders" />}>
        <Route path="auto-responders" element={<AutoRespondersAdmin />} />
      </Route>
      {/* /surveys is gated on the forms module. */}
      <Route element={<RequireModule moduleKey="forms" />}>
        <Route element={<RequireAdminPage page="eforms" />}>
          <Route path="eforms" element={<EFormsAdmin />} />
        </Route>
      </Route>

      {/* Reports group — the admin "Manage" list is gone. It showed report
          DEFINITIONS across users with a subset of the actions My Reports has,
          which made it a fourth place to look for the same reports. Everything
          it could do is now in My Reports; the URL redirects so bookmarks keep
          working. Not an Admin page, so it has no level. */}
      <Route element={<RequireModule moduleKey="reports" />}>
        <Route path="custom-reports" element={<Navigate to="/reports/my-reports" replace />} />
      </Route>

      {/* Marketing group */}
      <Route element={<RequireModule moduleKey="marketing" />}>
        <Route element={<RequireAdminPage page="marketing-integrations" />}>
          <Route path="marketing-integrations" element={<MarketingIntegrationsAdmin />} />
        </Route>
      </Route>

      {/* /admin itself and any unknown /admin/... URL (deleted pages included):
          no Admin landing page, so an account with every page Hidden has no
          Admin page at all. */}
      <Route path="*" element={<AdminFallback />} />
      </Route>
    </Routes>
  );
}
